import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  Button,
  Card,
  CardBody,
  Typography,
} from "@material-tailwind/react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useAuth } from "../AuthContext";
import { useMessage } from "../MessageContext";

const ResetPassword = ({ onClose, defaultAccountType = "owner" }) => {
  const { apiUrl, subscription_id } = useAuth();
  const msg = useMessage();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState(defaultAccountType);
  const [organizationId, setOrganizationId] = useState(subscription_id || ""); // ✅ default from subscription_id
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const isRequestingOtpRef = useRef(false);
  const isResettingPasswordRef = useRef(false);

  useEffect(() => {
    setOrganizationId(subscription_id || "");
  }, [subscription_id]);

  const requestOtp = async () => {
    if (isRequestingOtpRef.current) {
      return;
    }

    isRequestingOtpRef.current = true;
    setIsRequestingOtp(true);
    try {
      await axios.post(`${apiUrl}/api/${accountType === "user" ? "consumers" : "organizations"}/sendotp`, null, {
        params: { organization_id: organizationId, email },
      });
      toast.success(msg("auth.otpSent"));
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || msg("auth.otpSendFailed"));
    } finally {
      isRequestingOtpRef.current = false;
      setIsRequestingOtp(false);
    }
  };

  const resetPassword = async () => {
    if (isResettingPasswordRef.current) {
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(msg("auth.passwordsMismatch"));
      return;
    }

    isResettingPasswordRef.current = true;
    setIsResettingPassword(true);
    try {
      await axios.post(`${apiUrl}/api/${accountType === "user" ? "consumers" : "organizations"}/reset/password`, {
        ...(accountType === "user" ? { organization_id: Number(organizationId) } : { subscription_id: organizationId }),
        email,
        password: newPassword,
        oldpassword: otp,
      });
      toast.success(msg("auth.passwordReset"));
      setStep(1);
      setOrganizationId("");
      setEmail("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || msg("auth.passwordResetFailed")
      );
    } finally {
      isResettingPasswordRef.current = false;
      setIsResettingPassword(false);
    }
  };

  return (
    <Card className="w-96 mx-auto mt-10 shadow-lg">
      <CardBody>
        <div className="relative mb-4">
          <Typography variant="h4" className="text-center pr-8">
            Reset Password
          </Typography>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close reset password form"
              className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              &times;
            </button>
          )}
        </div>
        <ToastContainer
          position="top-center" // Change position as needed
          autoClose={3000} // Auto close in 3 seconds
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        {step === 1 ? (
          <>
            <div className="flex gap-2" role="group" aria-label="Account type">
              <Button size="sm" type="button" variant={accountType === "owner" ? "filled" : "outlined"} onClick={() => setAccountType("owner")} className="flex-1 normal-case">Organization owner</Button>
              <Button size="sm" type="button" variant={accountType === "user" ? "filled" : "outlined"} onClick={() => setAccountType("user")} className="flex-1 normal-case">Organization user</Button>
            </div>
            <Input
              label="Organization ID"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-3"
            />
            <Button
              onClick={requestOtp}
              disabled={isRequestingOtp}
              className="mt-5 w-full capitalize text-white bg-darkPurple hover:bg-lightPurple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              {isRequestingOtp ? "Sending OTP..." : "Send OTP"}
            </Button>
          </>
        ) : (
          <>
            <Input
              label="OTP"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-3"
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-3"
            />
            <Button
              onClick={resetPassword}
              disabled={isResettingPassword}
              className="mt-5 w-full capitalize text-white bg-darkPurple hover:bg-lightPurple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              {isResettingPassword ? "Updating password..." : "Reset Password"}
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ResetPassword;
